import React from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Breadcrumbs,
  Link,
  List,
  ListItem,
  ListItemText,
  useTheme,
} from '@mui/material';
import { Home, Verified } from '@mui/icons-material';
import { Link as RouterLink } from 'react-router-dom';
import { motion } from 'framer-motion';

const QualityAssurancePage = () => {
  const theme = useTheme();

  const sections = [
    {
      title: "Our Commitment to Quality",
      content: [
        "At Mall of Stone, quality is not a checkpoint — it is a philosophy embedded in every slab we source, process, and deliver.",
        "Each stone undergoes rigorous inspection to ensure it meets our standards for durability, aesthetics, and performance."
      ]
    },
    {
      title: "Carefully Sourced Materials",
      content: [
        "Stones are sourced from trusted quarries and suppliers across India and internationally.",
        "Every batch is evaluated for structural integrity, natural veining consistency, and color accuracy.",
        "We work only with partners who follow ethical and sustainable sourcing practices."
      ]
    },
    {
      title: "Multi-Level Quality Inspection",
      content: [
        "Initial inspection at sourcing and procurement stage",
        "Precision checks during cutting, polishing, and finishing",
        "Final inspection before packaging and dispatch",
        "Only stones that pass all quality benchmarks are approved for delivery"
      ]
    },
    {
      title: "Precision Processing & Finishing",
      content: [
        "Advanced cutting and finishing technology ensures dimensional accuracy.",
        "Surface finishes are tested for smoothness, durability, and consistency.",
        "Special attention is given to edges, thickness tolerance, and pattern alignment."
      ]
    },
    {
      title: "Performance & Application Checks",
      content: [
        "Slip resistance evaluation for bathrooms and outdoor areas",
        "Water absorption and stain resistance checks",
        "Strength and durability assessment based on intended usage",
        "Recommendations aligned with residential and commercial standards"
      ]
    },
    {
      title: "Secure Packaging & Handling",
      content: [
        "Each stone is carefully packed using protective materials to prevent transit damage.",
        "Crate and pallet packaging is designed to maintain structural safety during transport.",
        "Every shipment is labeled and handled with traceability and care."
      ]
    },
    {
      title: "Post-Delivery Support",
      content: [
        "Our quality assurance doesn’t end at delivery.",
        "Customers can report any concerns immediately upon receipt.",
        "Our team ensures swift resolution in line with our Returns & Refunds policy."
      ]
    },
    {
      title: "Why Our Quality Matters",
      content: [
        "Natural stone is an investment in longevity and elegance.",
        "Our quality assurance process ensures confidence, consistency, and peace of mind.",
        "Because exceptional spaces deserve materials that stand the test of time."
      ]
    }
  ];

  return (
    <Box sx={{ minHeight: '100vh', backgroundColor: 'background.default' }}>
      <Container maxWidth="lg" sx={{ py: 4 }}>

        {/* Breadcrumbs */}
        <Breadcrumbs sx={{ mb: 3 }}>
          <Link
            component={RouterLink}
            to="/"
            color="inherit"
            sx={{ display: 'flex', alignItems: 'center' }}
          >
            <Home sx={{ mr: 0.5 }} fontSize="inherit" />
            Home
          </Link>
          <Typography color="text.primary">Quality Assurance</Typography>
        </Breadcrumbs>

        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <Paper
            sx={{
              p: 4,
              mb: 4,
              background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
              color: 'white',
              textAlign: 'center',
            }}
          >
            <Verified sx={{ fontSize: 60, mb: 2, opacity: 0.9, color: 'white' }} />
            <Typography variant="h3" fontWeight="bold" sx={{ color: 'white' }}>
              Quality Assurance
            </Typography>
            <Typography
              variant="h6"
              sx={{ opacity: 0.9, maxWidth: 800, mx: 'auto', mt: 1, color: 'white' }}
            >
              Uncompromising standards that ensure every stone reflects excellence.
            </Typography>
          </Paper>
        </motion.div>

        {/* Sections */}
        {sections.map((section, index) => (
          <motion.div
            key={index}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 * index }}
          >
            <Paper sx={{ p: 4, mb: 3 }}>
              <Typography
                variant="h5"
                fontWeight="bold"
                sx={{ color: 'primary.main', mb: 2 }}
              >
                {section.title}
              </Typography>
              <List>
                {section.content.map((item, i) => (
                  <ListItem key={i} sx={{ px: 0 }}>
                    <ListItemText primary={item} />
                  </ListItem>
                ))}
              </List>
            </Paper>
          </motion.div>
        ))}
      </Container>
    </Box>
  );
};

export default QualityAssurancePage;
